import { FC, useState } from 'react'
import { Col } from 'antd'
import { useTranslation } from 'react-i18next'
import { EmployeeWithBuFragment, ReviewerOfEmployeeType, ReviewerOfEmployeesInput, ReviewerAndEmployee } from '../../../types'
import { PerModal } from '../../../components/custom_components/Modal'
import { EmployeeReviewerSelect } from '../../../components/custom_components/EmployeeReviewerSelect'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { createReviewerOfEmployeeRelation } from '../../../graphql/reviewerEmployee/hooks/useMutateReviewerOfEmployee'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'
import { showNotification } from '../../../components/custom_components/Notification/notification'

interface ReviewerAddModalProps {
  open: boolean
  subordinates: EmployeeWithBuFragment[]
  existingRelations: ReviewerAndEmployee[]
  onCancel: () => void
  buId: string
  companyId: string
  buName: string
}

export const BuReviewerAddModal: FC<ReviewerAddModalProps> = ({
  open,
  onCancel,
  existingRelations,
  subordinates,
  buId,
  companyId,
  buName,
}) => {
  const { t } = useTranslation()

  const [reviewer, setReviewer] = useState<EmployeeWithBuFragment | undefined>()
  // employee is actually reviewee but it's too similar so it might be confused with reviewer
  const [employee, setEmployee] = useState<EmployeeWithBuFragment | undefined>()

  const [saving, setSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const onSelectReviewer = async (selectedReviewer?: EmployeeWithBuFragment) => {
    if (!!selectedReviewer) {
      setReviewer(selectedReviewer)
    }
  }
  const onSelectEmployee = async (selectedEmployee?: EmployeeWithBuFragment) => {
    if (!!selectedEmployee) {
      setEmployee(selectedEmployee)
    }
  }

  const saveChanges = async () => {
    if (!!reviewer && !!employee) {
      setSaving(true)
      setErrorMessage('')

      const reviewType = ReviewerOfEmployeeType.Internal
      const revEmpInput: ReviewerOfEmployeesInput = {
        type: reviewType,
        reviewerId: reviewer.id,
        employeeId: employee.id,
        companyId: companyId,
        fromDate: new Date().toDateString(),
        toDate: null,
      }
      const result = await createReviewerOfEmployeeRelation(revEmpInput, buId)
      if (!!result.errors && !!result.errors.length) {
        const errorKey = result.errors?.[0].extensions?.validationErrors?.[0]?.errorKey
        const errorMessage = errorKey?.startsWith('error.code')
          ? result.errors?.[0].extensions?.validationErrors?.[0]?.message ||
            t('companySettingsView.reviewerTab.addModal.notifications.error.description')
          : t('companySettingsView.reviewerTab.addModal.notifications.error.description')

        setErrorMessage(errorMessage)
        setSaving(false)
      } else {
        resetModalForm()
        showNotification({
          type: 'success',
          message: t('companySettingsView.reviewerTab.addModal.notifications.success.title'),
          description: t('companySettingsView.reviewerTab.addModal.notifications.success.description'),
        })
      }
    }
  }

  const reviewerRelationAlreadyExists = existingRelations.some(
    (r) => reviewer !== undefined && employee !== undefined && r.reviewer.id === reviewer.id && r.employee.id === employee.id,
  )
  const inverseReviewerRelationAlreadyExists = existingRelations.some(
    (r) => reviewer !== undefined && employee !== undefined && r.reviewer.id === employee.id && r.employee.id === reviewer.id,
  )

  const resetModalForm = () => {
    setReviewer(undefined)
    setEmployee(undefined)
    setErrorMessage('')
    setSaving(false)
    onCancel()
  }

  return (
    <PerModal
      title={{ heading: `${t('companySettingsView.reviewerTab.addModal.heading')} ${buName}` }}
      size="large"
      bodyStyle={{ overflowY: 'auto' }}
      visible={open}
      onCancel={resetModalForm}
      footer={{
        cancelBtn: {
          onClick: resetModalForm,
        },
        confirmBtn: {
          onClick: saveChanges,
          disabled: !reviewer || !employee || reviewer.id === employee.id || reviewerRelationAlreadyExists || saving,
          isProcessing: { active: saving },
          tooltip:
            (!reviewer && t('companySettingsView.reviewerTab.addModal.saveBtn.tooltipReviewerMissing')) ||
            (!employee && t('companySettingsView.reviewerTab.addModal.saveBtn.tooltipEmployeeMissing')) ||
            (reviewer!.id === employee!.id && t('companySettingsView.reviewerTab.addModal.saveBtn.tooltipSameEmployee')) ||
            (reviewerRelationAlreadyExists && t('companySettingsView.reviewerTab.addModal.saveBtn.tooltipAlreadyExistsRelation')),
        },
      }}
    >
      <>
        <PerFlexRow direction="row" mb="s">
          <Col span={4}>
            <PerFlexRow align="middle" className="fullHeight">
              <PerText color="secondary">{t('companySettingsView.reviewerTab.addModal.reviewer.label')}</PerText>
            </PerFlexRow>
          </Col>
          <Col span={20}>
            <EmployeeReviewerSelect
              placeholderText={t('companySettingsView.reviewerTab.addModal.reviewer.placeholder')}
              notFoundContentText={t('companySettingsView.reviewerTab.addModal.reviewer.notFoundContent')}
              employeeId={reviewer ? reviewer.id : undefined}
              employeeData={subordinates}
              onChangeEmployee={(employee) => onSelectReviewer(employee)}
            />
          </Col>
        </PerFlexRow>

        <PerFlexRow direction="row">
          <Col span={4}>
            <PerFlexRow align="middle" className="fullHeight">
              <PerText color="secondary">{t('companySettingsView.reviewerTab.addModal.employee.label')}</PerText>
            </PerFlexRow>
          </Col>
          <Col span={20}>
            <EmployeeReviewerSelect
              placeholderText={t('companySettingsView.reviewerTab.addModal.employee.placeholder')}
              notFoundContentText={t('companySettingsView.reviewerTab.addModal.employee.notFoundContent')}
              employeeId={employee ? employee.id : undefined}
              employeeData={subordinates}
              onChangeEmployee={(employee) => onSelectEmployee(employee)}
            />
          </Col>
        </PerFlexRow>

        {inverseReviewerRelationAlreadyExists && (
          <PerFlexRow direction="row" align="middle" mt="m">
            <Col span="1">
              <IconIndicator icon="warning_fill" background="warning" size="l" />
            </Col>
            <Col span="23">
              <PerText color="secondary" fontSize="s">
                {t('companySettingsView.reviewerTab.addModal.inverseRelationshipeExists', {
                  reviewer: employee!.firstName,
                  reviewee: (reviewer as EmployeeWithBuFragment).firstName,
                })}
              </PerText>
            </Col>
          </PerFlexRow>
        )}

        {errorMessage && (
          <PerText color="error" fontSize="m" mt="m">
            {errorMessage}
          </PerText>
        )}
      </>
    </PerModal>
  )
}
